import {
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export const GRID_EDIT_START_EVENT = "grid-edit-start";

const DEFAULT_ROW_SELECTOR = "tr, [role='row']";
const DEFAULT_CELL_SELECTOR =
  "th, td, [role='gridcell'], [role='columnheader'], [role='rowheader']";
const DEFAULT_PAGE_SIZE = 10;

const WIDGET_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[contenteditable='true']",
  "[role='button']",
  "[tabindex='0']",
  "[data-grid-demoted]",
].join(", ");
const HIDDEN_ANCESTOR_SELECTOR = "[hidden], [aria-hidden='true'], [inert]";
const EDIT_CONTROL_SELECTOR =
  "input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled])";
const NON_TEXT_INPUT_TYPES = [
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "range",
  "color",
  "file",
  "image",
];
const SELECT_ON_EDIT_INPUT_TYPES = ["text", "number", "search", "email", "url"];
const OBSERVER_CONFIG: MutationObserverInit = {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: [
    "disabled",
    "hidden",
    "aria-hidden",
    "inert",
    "tabindex",
    "contenteditable",
  ],
};

export interface GridNavigationOptions {
  rowSelector?: string;
  cellSelector?: string;
  pageSize?: number;
  isKeyHandled?: (event: KeyboardEvent) => boolean;
  onActivateCell?: (cell: HTMLElement, event: KeyboardEvent) => boolean;
}

interface GridPosition {
  row: number;
  column: number;
}

interface GridModel {
  rows: HTMLElement[][];
}

export interface GridNavigation<E extends HTMLElement> {
  gridProps: {
    ref: (node: E | null) => void;
    role: "grid";
    onKeyDown: (event: KeyboardEvent<E>) => void;
    onFocus: (event: FocusEvent<E>) => void;
  };
  focusCell: (rowIndex: number, columnIndex: number) => void;
}

function isWidgetCandidate(element: HTMLElement, grid: HTMLElement): boolean {
  if (element.hasAttribute("data-grid-skip")) return false;
  const hiddenAncestor = element.closest(HIDDEN_ANCESTOR_SELECTOR);
  if (hiddenAncestor && grid.contains(hiddenAncestor)) return false;
  if ((element as HTMLButtonElement).disabled) return false;
  if (element instanceof HTMLInputElement && element.type === "hidden") {
    return false;
  }
  return true;
}

function getWidgets(cell: HTMLElement, grid: HTMLElement): HTMLElement[] {
  return Array.from(cell.querySelectorAll<HTMLElement>(WIDGET_SELECTOR)).filter(
    (element) => isWidgetCandidate(element, grid),
  );
}

function getTarget(cell: HTMLElement, grid: HTMLElement): HTMLElement {
  if (cell.hasAttribute("data-grid-edit")) return cell;
  const widgets = getWidgets(cell, grid);
  const primary = widgets.find((widget) =>
    widget.hasAttribute("data-grid-primary"),
  );
  if (primary) return primary;
  if (widgets.length === 1) return widgets[0];
  return cell;
}

function isTextEntry(element: HTMLElement): boolean {
  if (element instanceof HTMLTextAreaElement) return true;
  if (element instanceof HTMLSelectElement) return true;
  if (element instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.includes(element.type);
  }
  return element.isContentEditable;
}

function setTabIndex(element: HTMLElement, value: "0" | "-1") {
  if (value === "-1" && !element.hasAttribute("data-grid-demoted")) {
    const isManaged =
      element.getAttribute("tabindex") === "0" ||
      element.matches(
        "a[href], button, input, select, textarea, [role='button']",
      );
    if (isManaged) element.setAttribute("data-grid-demoted", "");
  }
  if (element.getAttribute("tabindex") !== value) {
    element.setAttribute("tabindex", value);
  }
}

export function useGridNavigation<E extends HTMLElement = HTMLElement>(
  options: GridNavigationOptions = {},
): GridNavigation<E> {
  const gridRef = useRef<E | null>(null);
  const [gridElement, setGridElement] = useState<E | null>(null);
  const attachGrid = useCallback((node: E | null) => {
    gridRef.current = node;
    setGridElement(node);
  }, []);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const activeRef = useRef<GridPosition>({ row: 0, column: 0 });
  const observerRef = useRef<MutationObserver | null>(null);
  const focusWasInside = useRef(false);
  const recoveryRef = useRef<{
    element: HTMLElement;
    wanted: GridPosition;
  } | null>(null);

  const readModel = useCallback((): GridModel => {
    const grid = gridRef.current;
    const {
      rowSelector = DEFAULT_ROW_SELECTOR,
      cellSelector = DEFAULT_CELL_SELECTOR,
    } = optionsRef.current;
    if (!grid) return { rows: [] };
    const rows = Array.from(grid.querySelectorAll<HTMLElement>(rowSelector))
      .map((row) =>
        Array.from(row.children).filter((child): child is HTMLElement =>
          child.matches(cellSelector),
        ),
      )
      .filter((cells) => cells.length > 0);
    return { rows };
  }, []);

  const clampPosition = useCallback(
    (model: GridModel, position: GridPosition) => {
      const row = Math.min(Math.max(position.row, 0), model.rows.length - 1);
      if (row < 0) return { row: 0, column: 0 };
      const column = Math.min(
        Math.max(position.column, 0),
        model.rows[row].length - 1,
      );
      return { row, column };
    },
    [],
  );

  const locate = useCallback(
    (model: GridModel, element: HTMLElement): GridPosition | null => {
      const grid = gridRef.current;
      const { cellSelector = DEFAULT_CELL_SELECTOR } = optionsRef.current;
      let cell = element.closest<HTMLElement>(cellSelector);
      while (cell && grid && !grid.contains(cell)) {
        cell = cell.parentElement?.closest<HTMLElement>(cellSelector) ?? null;
      }
      if (!cell) return null;
      for (let row = 0; row < model.rows.length; row += 1) {
        const column = model.rows[row].indexOf(cell);
        if (column >= 0) return { row, column };
      }
      return null;
    },
    [],
  );

  const applyRoving = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const observer = observerRef.current;
    observer?.disconnect();
    const model = readModel();
    const active = clampPosition(model, activeRef.current);
    model.rows.forEach((cells, rowIndex) => {
      cells.forEach((cell, columnIndex) => {
        const isActive =
          rowIndex === active.row && columnIndex === active.column;
        const target = getTarget(cell, grid);
        const focusInsideCell =
          isActive && cell.contains(document.activeElement);
        setTabIndex(cell, isActive && target === cell ? "0" : "-1");
        getWidgets(cell, grid).forEach((widget) =>
          setTabIndex(
            widget,
            isActive && (widget === target || focusInsideCell) ? "0" : "-1",
          ),
        );
      });
    });
    const activeElement = document.activeElement;
    const focusLost =
      focusWasInside.current &&
      (!activeElement || activeElement === document.body);
    const recovery = recoveryRef.current;
    if (recovery) {
      const wanted = recovery.wanted;
      const wantedAvailable =
        model.rows.length > wanted.row &&
        model.rows[wanted.row].length > wanted.column;
      if (document.activeElement !== recovery.element) {
        recoveryRef.current = null;
      } else if (wantedAvailable) {
        recoveryRef.current = null;
        getTarget(model.rows[wanted.row][wanted.column], grid).focus();
      }
    } else if (focusLost && model.rows.length > 0) {
      const wanted = activeRef.current;
      const element = getTarget(model.rows[active.row][active.column], grid);
      if (active.row !== wanted.row || active.column !== wanted.column) {
        recoveryRef.current = { element, wanted };
      }
      element.focus();
    }
    observer?.observe(grid, OBSERVER_CONFIG);
  }, [clampPosition, readModel]);

  useLayoutEffect(() => {
    const grid = gridElement;
    if (!grid) return;
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        applyRoving();
      });
    });
    observerRef.current = observer;
    const handleFocusOut = (event: globalThis.FocusEvent) => {
      const source = event.target as HTMLElement;
      queueMicrotask(() => {
        if (source.isConnected && !grid.contains(document.activeElement)) {
          focusWasInside.current = false;
        }
        applyRoving();
      });
    };
    grid.addEventListener("focusout", handleFocusOut);
    observer.observe(grid, OBSERVER_CONFIG);
    return () => {
      grid.removeEventListener("focusout", handleFocusOut);
      observer.disconnect();
      observerRef.current = null;
    };
  }, [applyRoving, gridElement]);

  useLayoutEffect(() => {
    applyRoving();
  });

  const focusPosition = useCallback(
    (position: GridPosition) => {
      const model = readModel();
      if (model.rows.length === 0) return;
      const grid = gridRef.current;
      if (!grid) return;
      activeRef.current = clampPosition(model, position);
      applyRoving();
      const { row, column } = activeRef.current;
      getTarget(model.rows[row][column], grid).focus();
    },
    [applyRoving, clampPosition, readModel],
  );

  const startEdit = useCallback((cell: HTMLElement) => {
    const control = cell.querySelector<HTMLElement>(EDIT_CONTROL_SELECTOR);
    if (!control) return false;
    control.focus();
    if (
      control instanceof HTMLInputElement &&
      SELECT_ON_EDIT_INPUT_TYPES.includes(control.type)
    ) {
      control.select();
    }
    control.dispatchEvent(new CustomEvent(GRID_EDIT_START_EVENT));
    return true;
  }, []);

  const nextPosition = useCallback(
    (
      model: GridModel,
      from: GridPosition,
      event: KeyboardEvent,
    ): GridPosition | null => {
      const pageSize = optionsRef.current.pageSize ?? DEFAULT_PAGE_SIZE;
      const lastRow = model.rows.length - 1;
      const lastColumn = model.rows[from.row].length - 1;
      if (event.ctrlKey) {
        if (event.key === "Home") return { row: 0, column: 0 };
        if (event.key === "End") {
          return { row: lastRow, column: model.rows[lastRow].length - 1 };
        }
        return null;
      }
      switch (event.key) {
        case "ArrowLeft":
          return { row: from.row, column: from.column - 1 };
        case "ArrowRight":
          return { row: from.row, column: from.column + 1 };
        case "ArrowUp":
          return { row: from.row - 1, column: from.column };
        case "ArrowDown":
          return { row: from.row + 1, column: from.column };
        case "Home":
          return { row: from.row, column: 0 };
        case "End":
          return { row: from.row, column: lastColumn };
        case "PageUp":
          return { row: from.row - pageSize, column: from.column };
        case "PageDown":
          return { row: from.row + pageSize, column: from.column };
        default:
          return null;
      }
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<E>) => {
      recoveryRef.current = null;
      if (event.defaultPrevented) return;
      if (optionsRef.current.isKeyHandled?.(event)) return;
      if (event.altKey || event.metaKey) return;
      const target = event.target as HTMLElement;
      const model = readModel();
      const position = locate(model, target);
      if (!position) return;
      const cell = model.rows[position.row][position.column];

      if (event.key === "Enter" || event.key === "F2" || event.key === " ") {
        if (event.shiftKey || event.ctrlKey) return;
        if (target !== cell) return;
        if (cell.hasAttribute("data-grid-edit")) {
          if (event.key === " ") return;
          if (startEdit(cell)) event.preventDefault();
          return;
        }
        if (event.key === "F2") return;
        if (getWidgets(cell, gridRef.current as HTMLElement).length > 0) return;
        const consumed = optionsRef.current.onActivateCell?.(cell, event);
        if (consumed) event.preventDefault();
        return;
      }

      if (isTextEntry(target)) return;
      if (event.shiftKey) return;
      const next = nextPosition(model, position, event);
      if (!next) return;
      event.preventDefault();
      focusPosition(next);
    },
    [focusPosition, locate, nextPosition, readModel, startEdit],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<E>) => {
      if (!gridRef.current?.contains(event.target as Node)) return;
      focusWasInside.current = true;
      if (recoveryRef.current?.element === event.target) {
        applyRoving();
        return;
      }
      const model = readModel();
      const position = locate(model, event.target as HTMLElement);
      if (position) activeRef.current = position;
      applyRoving();
    },
    [applyRoving, locate, readModel],
  );

  const focusCell = useCallback(
    (rowIndex: number, columnIndex: number) =>
      focusPosition({ row: rowIndex, column: columnIndex }),
    [focusPosition],
  );

  return {
    gridProps: {
      ref: attachGrid,
      role: "grid",
      onKeyDown: handleKeyDown,
      onFocus: handleFocus,
    },
    focusCell,
  };
}
